import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import useNavigateWithBase from "../hooks/useNavigateWithBase"; // ✅ 新しいフックをインポート
import '../CSS/LoginPage.css';

const LoginPage = () => {
  const navigate = useNavigateWithBase();
  const [companyCode, setCompanyCode] = useState('');
  const [personalId, setPersonalId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockUntil, setLockUntil] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false); // ✅ ログイン成功フラグ

  // ✅ `useEffect()` でログイン成功後に遅延リダイレクト
  useEffect(() => {
    if (loginSuccess) {
      setTimeout(() => {
        navigate('/portal'); // 🔥 ここで遅延実行
      }, 100); // 100ms 遅延して競合を回避
    }
  }, [loginSuccess, navigate]);

  // 正規表現バリデーション関数 (useCallbackでメモ化)
  const validateInputs = useCallback(() => {
    const companyCodeRegex = /^[a-zA-Z0-9]{4}$/;
    const personalIdRegex = /^[a-zA-Z0-9]{4}$/;
    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])[a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{8,16}$/;

    return (
      companyCodeRegex.test(companyCode) &&
      personalIdRegex.test(personalId) &&
      passwordRegex.test(password)
    );
  }, [companyCode, personalId, password]);

  // バリデーションを監視
  useEffect(() => {
    const isValidInput = validateInputs();
    setIsValid(isValidInput);
  }, [validateInputs]);

  // ロック状態の監視
  useEffect(() => {
    if (lockUntil) {
      const interval = setInterval(() => {
        if (Date.now() >= lockUntil) {
          setLockUntil(null);
          setFailedAttempts(0);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [lockUntil]);

  // ログイン処理
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (lockUntil && Date.now() < lockUntil) {
      setError('ログインがロックされています。数分後にお試しください。');
      setIsLoading(false);
      return;
    }

    if (!isValid) {
      setError('入力内容を確認してください。');
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        'https://ky6c3k6vad.execute-api.ap-northeast-3.amazonaws.com/prod/api/login',
        { companyCode, personalId, password }
      );

      if (response.data.token && response.data.role && response.data.name) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('role', response.data.role);
        localStorage.setItem('company_code', response.data.company_code); // ✅ 追加
        localStorage.setItem('company_name', response.data.company_name);  // 🔹 会社名を保存
        localStorage.setItem('loggedInPersonalId', response.data.personalId); // ✅ 別名で保存
        localStorage.setItem('loggedInUserName', response.data.name); // ✅ 別名で保存
        localStorage.removeItem('project_id');
        
        setLoginSuccess(true);
      } else {
        throw new Error('認証失敗');
      }
    } catch (err) {
      if (!err.response) {
        setError('サーバに接続できません。ネットワークを確認してください。');
      } else if (err.response.status === 401) {
        setFailedAttempts((prev) => prev + 1);
        if (failedAttempts + 1 >= 3) {
          setLockUntil(Date.now() + 5 * 60 * 1000); // 5分ロック
          setError('ログインに3回失敗しました。数分後に再試行してください。');
        } else {
          setError(`ログインに失敗しました。残り試行回数: ${3 - (failedAttempts + 1)}`);
        }
      } else {
        setError('予期しないエラーが発生しました。');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1 className="login-title">ログイン</h1>

        {error && <p className="error-message">{error}</p>}

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="companyCode" className="form-label">ログインID</label>
            <div className="form-inline">
              <input
                type="text"
                id="companyCode"
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value)}
                className="form-input"
                placeholder="会社コード"
                disabled={isLoading}
              />
              <span className="separator">-</span>
              <input
                type="text"
                id="personalId"
                value={personalId}
                onChange={(e) => setPersonalId(e.target.value)}
                className="form-input"
                placeholder="個人ID"
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">パスワード</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input-password"
              placeholder="パスワード"
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isLoading || !isValid || (lockUntil && Date.now() < lockUntil)}
          >
            {isLoading ? 'ログイン処理中...' : 'ログイン'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
